/* eslint-disable no-unused-vars */
// scenarioState = base roadmap / normal flow.
// resolveCustomFlowModifiers = dito ka magsisingit ng extra statuses depende sa boolean logic.
// resolveNextStatus / resolveNextAction = taga-compute na lang ng actual next button/status.
// TicketDialog = display/action consumer na lang.

import { taptapCouriers } from '../constants';
import {
  TAG_AS_RESERVED,
  scenarioState,
  taptapStatuses,
  taptapSourceV2
} from '../constants/taptap-flow-process';
import { ECOMM_PAYMENT_TYPES } from '../constants/taptap-payments';
import { taptapServiceType } from '../constants/taptap-service-type';
import { TapTapCustomerData } from '../types';

export type TaptapFlowScenario = typeof scenarioState[number];

export type TaptapFlowOptions = {
  hasSystemGeneratedTicket?: boolean;
  paymentTotal?: number;
};

export type TaptapTicketScenario = {
  courier: string | null;
  serviceType: string | null;
  source: string | null;
};

export type TaptapNextAction = {
  status: string | null;
  label: string;
  disabled: boolean;
  disabledReason: string;
};

export const TAPTAP_CREATE_SALES_TRANSFER_TRANSACTION_ACTION =
  'CREATE_SALES_TRANSFER_TRANSACTION';

type FlowModifier = {
  enabled: boolean;
  status: string;
  insertBefore?: string;
  insertAfter?: string;
};

const isOnlineSource = (source?: string) =>
  source === taptapSourceV2.FACEBOOK || source === taptapSourceV2.VIBER;

const hasOnlineTransferPayment = (customer?: TapTapCustomerData) =>
  customer?.customers_payments?.some((payment) =>
    ECOMM_PAYMENT_TYPES.has((payment?.payment_type || '').toUpperCase())
  ) || false;

const isCodTechoutTicket = (customer?: TapTapCustomerData) =>
  customer?.courier === taptapCouriers.COD_TECHOUT;

const isSalesTransferTicket = (customer?: TapTapCustomerData) =>
  [
    taptapServiceType.BUILD_SALES_TRANSFER,
    taptapServiceType.PARTS_OUT_SALES_TRANSFER
  ].includes(customer?.service_type || '');

// Normalizes customer fields into the source/service/courier key used by scenarioState.
export const resolveTicketScenario = (
  customer?: TapTapCustomerData
): TaptapTicketScenario => ({
  courier: isCodTechoutTicket(customer)
    ? taptapCouriers.TECHOUT
    : customer?.courier ?? null,
  serviceType: customer?.service_type ?? null,
  source: isOnlineSource(customer?.source)
    ? taptapSourceV2.ONLINE
    : customer?.source ?? null
});

// Finds the base roadmap from scenarioState. This should stay as the source of truth.
export const resolveCurrentScenario = (
  ticketScenario: TaptapTicketScenario
) => {
  return scenarioState.find((data) => {
    return (
      data.courier === ticketScenario.courier &&
      data.serviceType === ticketScenario.serviceType &&
      data.source === ticketScenario.source
    );
  });
};

const applyFlowModifiers = (
  statusFlow: string[],
  modifiers: FlowModifier[]
) => {
  const flow = [...statusFlow];

  const insertBeforeStatus = (statusToInsert: string, beforeStatus: string) => {
    if (flow.includes(statusToInsert)) {
      return;
    }

    const beforeIndex = flow.indexOf(beforeStatus);
    if (beforeIndex >= 0) {
      flow.splice(beforeIndex, 0, statusToInsert);
      return;
    }

    flow.push(statusToInsert);
  };

  const insertAfterStatus = (statusToInsert: string, afterStatus: string) => {
    if (flow.includes(statusToInsert)) {
      return;
    }

    const afterIndex = flow.indexOf(afterStatus);
    if (afterIndex >= 0) {
      flow.splice(afterIndex + 1, 0, statusToInsert);
      return;
    }

    flow.push(statusToInsert);
  };

  modifiers.forEach((modifier) => {
    if (!modifier.enabled) {
      return;
    }

    if (modifier.insertBefore) {
      insertBeforeStatus(modifier.status, modifier.insertBefore);
      return;
    }

    if (modifier.insertAfter) {
      insertAfterStatus(modifier.status, modifier.insertAfter);
    }
  });

  return flow;
};

// Lists the boolean-driven statuses to insert into the base roadmap for this ticket.
const resolveCustomFlowModifiers = ({
  customer,
  options
}: {
  customer?: TapTapCustomerData;
  options?: TaptapFlowOptions;
}): FlowModifier[] => {
  const isReservedTicket = customer?.tag === TAG_AS_RESERVED;
  const isReservedNonBuildTicket =
    isReservedTicket && customer?.service_type !== taptapServiceType.FOR_BUILD;
  const hasTestingRequired = Boolean(options?.hasSystemGeneratedTicket);
  const shouldInsertPaymentCollection = isCodTechoutTicket(customer);

  const shouldInsertTestingStatus =
    hasTestingRequired &&
    (customer?.service_type === taptapServiceType.PARTS_OUT ||
      isReservedNonBuildTicket) &&
    [taptapStatuses.RELEASING, taptapStatuses.TESTING].includes(
      customer?.status || ''
    );

  // Add custom flow booleans here. Example: const shouldInsertQa = Boolean(...);
  // Then add one object below with enabled, status, and insertBefore/insertAfter.
  // Keep custom status decisions here so TicketDialog only renders the resolved nextAction.
  return [
    {
      enabled: Boolean(shouldInsertTestingStatus),
      status: taptapStatuses.TESTING,
      insertAfter: taptapStatuses.RELEASING
    },
    {
      enabled: isReservedTicket,
      status: taptapStatuses.RESERVED,
      insertBefore: taptapStatuses.RELEASING
    },
    {
      enabled: shouldInsertPaymentCollection,
      status: taptapStatuses.PAYMENT_COLLECTION,
      insertBefore: taptapStatuses.COMPLETED
    }
  ];
};

// Combines scenarioState.statusFlow with custom insertions to produce the final flow.
export const resolveEffectiveStatusFlow = ({
  customer,
  currentScenario,
  options
}: {
  customer?: TapTapCustomerData;
  currentScenario?: TaptapFlowScenario;
  options?: TaptapFlowOptions;
}) => {
  const flowModifiers = resolveCustomFlowModifiers({ customer, options });

  return applyFlowModifiers(currentScenario?.statusFlow || [], flowModifiers);
};

// Checks the 20% DP gate used by reserved FOR BUILD tickets.
export const hasReservedBuildMinimumDp = (
  customer?: TapTapCustomerData,
  paymentTotal?: number
) => {
  const totalAmount = customer?.total_amount || 0;
  const paidAmount = Math.max(customer?.paid_amount || 0, paymentTotal || 0);

  if (totalAmount <= 0) {
    return false;
  }

  return paidAmount >= totalAmount * 0.2;
};

const hasReservedBuildPaidAmount = (
  customer?: TapTapCustomerData,
  paymentTotal?: number
) => {
  const paidAmount = Math.max(customer?.paid_amount || 0, paymentTotal || 0);

  return paidAmount > 0;
};

const hasReservedFullPayment = (
  customer?: TapTapCustomerData,
  paymentTotal?: number
) => {
  const totalAmount = customer?.total_amount || 0;
  const paidAmount = Math.max(customer?.paid_amount || 0, paymentTotal || 0);

  if (totalAmount <= 0) {
    return false;
  }

  return Boolean(customer?.balance === 0 || paidAmount >= totalAmount);
};

//check in time line if the ticket is already looped to technical department
const hasReservedBuildReachedTechnicalWork = (
  customer?: TapTapCustomerData
) => {
  const systemGeneratedTechnicalStatuses = [
    taptapStatuses.BUILDING,
    taptapStatuses.TESTING
  ];

  return Boolean(
    customer?.build_started_at ||
      customer?.timeline?.some(
        (timeline) =>
          timeline?.status &&
          systemGeneratedTechnicalStatuses.includes(timeline.status)
      )
  );
};

const hasStatusInTimeline = (customer?: TapTapCustomerData, status?: string) =>
  Boolean(
    status &&
      customer?.timeline?.some((timeline) => timeline?.status === status)
  );

// Handles reserved FOR BUILD next-status exceptions before falling back to normal flow order.
export const resolveReservedBuildNextStatus = ({
  customer,
  paymentTotal
}: {
  customer?: TapTapCustomerData;
  paymentTotal?: number;
}) => {
  const isReservedBuildTicket =
    customer?.tag === TAG_AS_RESERVED &&
    customer?.service_type === taptapServiceType.FOR_BUILD;

  if (!isReservedBuildTicket) {
    return undefined;
  }

  const hasBalance = Boolean(
    customer?.balance && Math.round(customer.balance * 100) / 100 > 0
  );
  const hasPaidAmount = hasReservedBuildPaidAmount(customer, paymentTotal);
  const hasMinimumDp = hasReservedBuildMinimumDp(customer, paymentTotal);
  const isCodTechout = isCodTechoutTicket(customer);
  const canCollectCodTechoutBalance = isCodTechout && hasMinimumDp;
  const isPickup = customer?.courier === taptapCouriers?.PICK_UP;
  const readyStatus = isPickup
    ? taptapStatuses.READY_FOR_PICKUP
    : taptapStatuses.FOR_PACKING;

  switch (customer?.status) {
    case taptapStatuses.FOR_PAYMENT:
      return hasMinimumDp
        ? taptapStatuses.ONLINE_FUND_TRANSFER
        : hasPaidAmount
        ? taptapStatuses.RESERVED
        : null;
    case taptapStatuses.ONLINE_FUND_TRANSFER:
      return taptapStatuses.PREPARING_ITEMS;
    case taptapStatuses.PREPARING_ITEMS:
      return taptapStatuses.RELEASING;
    case taptapStatuses.RELEASING:
      return taptapStatuses.ON_QUEUE;
    case taptapStatuses.ON_QUEUE:
      return taptapStatuses.BUILDING;
    case taptapStatuses.BUILDING:
      return hasBalance ? taptapStatuses.RESERVED : readyStatus;
    //RMA change item can occur here, so we need to check if the ticket is looped back to technical work through timeline or current status
    case taptapStatuses.RMA_CHANGED_ITEM:
      return taptapStatuses.PREPARING_ITEMS;
    case taptapStatuses.RESERVED:
      if (
        hasBalance &&
        hasReservedBuildReachedTechnicalWork(customer) &&
        !canCollectCodTechoutBalance
      ) {
        return null;
      }

      if (hasBalance && !hasMinimumDp) {
        return null;
      }

      return hasReservedBuildReachedTechnicalWork(customer)
        ? readyStatus
        : taptapStatuses.ON_QUEUE;
    case taptapStatuses.FOR_PACKING:
      return hasBalance && !canCollectCodTechoutBalance
        ? null
        : taptapStatuses.READY_FOR_DELIVERY;
    case taptapStatuses.READY_FOR_PICKUP:
    case taptapStatuses.READY_FOR_DELIVERY:
      if (hasBalance && canCollectCodTechoutBalance) {
        return taptapStatuses.PAYMENT_COLLECTION;
      }

      return hasBalance ? null : taptapStatuses.COMPLETED;
    case taptapStatuses.PAYMENT_COLLECTION:
      return hasBalance ? null : taptapStatuses.COMPLETED;
    default:
      return undefined;
  }
};

//Reserved item but not on build
const resolveReservedNonBuildNextStatus = ({
  customer,
  hasSystemGeneratedTicket,
  paymentTotal
}: {
  customer?: TapTapCustomerData;
  hasSystemGeneratedTicket?: boolean;
  paymentTotal?: number;
}) => {
  const isReservedNonBuildTicket =
    customer?.tag === TAG_AS_RESERVED &&
    customer?.service_type !== taptapServiceType.FOR_BUILD;

  if (!isReservedNonBuildTicket) {
    return undefined;
  }

  const hasBalance = Boolean(
    customer?.balance && Math.round(customer.balance * 100) / 100 > 0
  );
  const hasMinimumDp = hasReservedBuildMinimumDp(customer, paymentTotal);
  const isPickup = customer?.courier === taptapCouriers?.PICK_UP;
  const readyStatus = isPickup
    ? taptapStatuses.READY_FOR_PICKUP
    : taptapStatuses.FOR_PACKING;
  const hasTestingRequired = Boolean(hasSystemGeneratedTicket);
  const hasTestingDone = hasStatusInTimeline(customer, taptapStatuses.TESTING);
  const hasPaidAmount = hasReservedBuildPaidAmount(customer, paymentTotal);

  switch (customer?.status) {
    case taptapStatuses.FOR_PAYMENT:
      return hasMinimumDp
        ? taptapStatuses.PREPARING_ITEMS
        : hasPaidAmount
        ? taptapStatuses.RESERVED
        : null;
    case taptapStatuses.PREPARING_ITEMS:
      return taptapStatuses.RELEASING;
    case taptapStatuses.RELEASING:
      return hasTestingRequired
        ? taptapStatuses.TESTING
        : taptapStatuses.RESERVED;
    case taptapStatuses.TESTING:
      return isReservedNonBuildTicket
        ? taptapStatuses.RESERVED
        : taptapStatuses.FOR_PACKING;
    //RMA change item can occur here, so we need to check if the ticket is looped back to technical work through timeline or current status
    case taptapStatuses.RMA_CHANGED_ITEM:
      return taptapStatuses.PREPARING_ITEMS;
    case taptapStatuses.RESERVED:
      return hasBalance
        ? null
        : hasTestingRequired && !hasTestingDone
        ? taptapStatuses.TESTING
        : readyStatus;
    case taptapStatuses.FOR_PACKING:
      return hasBalance ? null : taptapStatuses.READY_FOR_DELIVERY;
    case taptapStatuses.READY_FOR_PICKUP:
    case taptapStatuses.READY_FOR_DELIVERY:
      return hasBalance ? null : taptapStatuses.COMPLETED;
    default:
      return undefined;
  }
};

// Resolves the next status from the final flow and skips payment verification outside that view.
export const resolveNextStatus = ({
  customer,
  effectiveStatusFlow,
  hasSystemGeneratedTicket,
  isPaymentVerificationView,
  paymentTotal
}: {
  customer?: TapTapCustomerData;
  effectiveStatusFlow: string[];
  hasSystemGeneratedTicket?: boolean;
  isPaymentVerificationView: boolean;
  paymentTotal?: number;
}) => {
  const reservedBuildNextStatus = resolveReservedBuildNextStatus({
    customer,
    paymentTotal
  });

  if (reservedBuildNextStatus !== undefined) {
    return reservedBuildNextStatus;
  }

  const reservedNonBuildNextStatus = resolveReservedNonBuildNextStatus({
    customer,
    hasSystemGeneratedTicket,
    paymentTotal
  });

  if (reservedNonBuildNextStatus !== undefined) {
    return reservedNonBuildNextStatus;
  }

  if (customer?.status === taptapStatuses.RMA_CHANGED_ITEM) {
    return taptapStatuses.PREPARING_ITEMS;
  }

  const currentStatusIndex = effectiveStatusFlow.indexOf(
    customer?.status || ''
  );

  if (currentStatusIndex < 0) {
    return null;
  }

  let nextIndex = currentStatusIndex + 1;

  while (
    !isPaymentVerificationView &&
    effectiveStatusFlow[nextIndex] === taptapStatuses.ONLINE_FUND_TRANSFER
  ) {
    nextIndex += 1;
  }

  return effectiveStatusFlow[nextIndex] ?? null;
};

// Turns nextStatus plus blockers into the ready-to-render TicketDialog button state.
export const resolveNextAction = ({
  customer,
  nextStatus,
  canProceed,
  isReadOnly,
  isStatusLoading,
  paymentTotal
}: {
  customer?: TapTapCustomerData;
  nextStatus: string | null;
  canProceed: boolean;
  isReadOnly?: boolean;
  isStatusLoading?: boolean;
  paymentTotal?: number;
}): TaptapNextAction => {
  const isCompleted = customer?.status === taptapStatuses.COMPLETED;
  const isReserved = customer?.tag === TAG_AS_RESERVED;
  const isReservedBuild =
    isReserved && customer?.service_type === taptapServiceType.FOR_BUILD;
  const isReservedNonBuild =
    isReserved && customer?.service_type !== taptapServiceType.FOR_BUILD;
  const isCodTechout = isCodTechoutTicket(customer);
  const hasBalance = Boolean(
    customer?.balance && Math.round(customer.balance * 100) / 100 > 0
  );
  const hasReservedBuildMinDp = hasReservedBuildMinimumDp(
    customer,
    paymentTotal
  );
  const canCollectReservedBuildCodTechoutBalance =
    isReservedBuild && isCodTechout && hasReservedBuildMinDp;
  const hasReachedTechnicalWork = hasReservedBuildReachedTechnicalWork(
    customer
  );
  const isPendingSalesTransferForPayment =
    customer?.status === taptapStatuses.PENDING_SALES_TRANSFER &&
    nextStatus === taptapStatuses.FOR_PAYMENT;
  const isSalesTransfer = isSalesTransferTicket(customer);
  const isSalesTransferMissingTransaction =
    isPendingSalesTransferForPayment &&
    isSalesTransfer &&
    !customer?.internal_transaction_no;
  const isBlockedBySalesTransferMinimumDp =
    isSalesTransfer &&
    customer?.status === taptapStatuses.FOR_PAYMENT &&
    !hasReservedBuildMinDp;
  const isBlockedBySalesTransferBalance =
    isSalesTransfer &&
    customer?.status === taptapStatuses.SALES_TRANSFER_RELEASING &&
    hasBalance;
  const isReservedBuildWithPendingBalance =
    isReservedBuild &&
    ([
      taptapStatuses.FOR_PACKING,
      taptapStatuses.READY_FOR_PICKUP,
      taptapStatuses.READY_FOR_DELIVERY
    ].includes(customer?.status || '') ||
      (customer?.status === taptapStatuses.RESERVED &&
        hasReachedTechnicalWork)) &&
    hasBalance &&
    !canCollectReservedBuildCodTechoutBalance;
  const isBlockedByBalance =
    hasBalance &&
    !isPendingSalesTransferForPayment &&
    !isSalesTransfer &&
    !isReserved &&
    !(isCodTechout && customer?.status !== taptapStatuses.PAYMENT_COLLECTION);
  const isBlockedByReservedMinimumDp =
    isReservedBuild &&
    customer?.status === taptapStatuses.RESERVED &&
    !hasReservedBuildMinDp;
  const shouldUsePaymentAction =
    customer?.status === taptapStatuses.FOR_PAYMENT && !isReserved;
  const hasPaidAmount = hasReservedBuildPaidAmount(customer, paymentTotal);
  const hasFullPayment = hasReservedFullPayment(customer, paymentTotal);
  const isReservedNonBuildWaitingForFullPayment =
    isReservedNonBuild &&
    customer?.status === taptapStatuses.RESERVED &&
    !hasFullPayment;

  const status = shouldUsePaymentAction
    ? hasOnlineTransferPayment(customer)
      ? taptapStatuses.ONLINE_FUND_TRANSFER
      : taptapStatuses.DONE_PAYMENT
    : isSalesTransferMissingTransaction
    ? TAPTAP_CREATE_SALES_TRANSFER_TRANSACTION_ACTION
    : nextStatus;

  const label =
    status === TAPTAP_CREATE_SALES_TRANSFER_TRANSACTION_ACTION
      ? 'Create Transaction'
      : status === taptapStatuses.ONLINE_FUND_TRANSFER
      ? 'FOR PAYMENT VERIFICATION'
      : status
      ? `Proceed ${status}`
      : 'Proceed';
  const isCreateSalesTransferTransactionAction =
    status === TAPTAP_CREATE_SALES_TRANSFER_TRANSACTION_ACTION;

  let disabledReason = '';
  if (isBlockedByBalance) {
    disabledReason = 'Please settle the balance first.';
  } else if (isBlockedBySalesTransferBalance) {
    disabledReason =
      'Sales transfer tickets need full payment before proceeding.';
  } else if (isReservedBuildWithPendingBalance) {
    disabledReason = 'Please settle the reserved balance first.';
  } else if (isBlockedByReservedMinimumDp) {
    disabledReason =
      'Reserved tickets need at least 20% down payment to proceed or full payment.';
  } else if (isBlockedBySalesTransferMinimumDp) {
    disabledReason =
      'Sales transfer tickets need at least 20% down payment to proceed.';
  } else if (isReservedBuild && !hasPaidAmount) {
    disabledReason = 'Please add down payment to proceed next status';
  } else if (isReservedNonBuild && !hasPaidAmount) {
    disabledReason = 'Please add down payment to proceed next status';
  } else if (isReservedNonBuildWaitingForFullPayment) {
    disabledReason = 'Please add full payment to proceed next status';
  } else if (
    !isPendingSalesTransferForPayment &&
    !isSalesTransfer &&
    !isReserved &&
    !isCodTechout &&
    (!hasPaidAmount || hasBalance)
  ) {
    disabledReason = 'Please add payment to proceed next status';
  } else if (!isCreateSalesTransferTransactionAction && !canProceed) {
    disabledReason = 'Cannot proceed at this stage.';
  }

  return {
    status,
    label,
    disabled:
      Boolean(isReadOnly) ||
      Boolean(isStatusLoading) ||
      isCompleted ||
      !status ||
      (!isCreateSalesTransferTransactionAction && !canProceed) ||
      Boolean(disabledReason),
    disabledReason
  };
};
